import { AppCategory } from './app.model';

export interface Embedded {
    users?: { [id: string]: EmbeddedUser };
    projects?: { [id: string]: EmbeddedProject };
    companies?: { [id: string]: EmbeddedCompany };
    cells?: { [id: string]: EmbeddedCell };
    apps?: { [id: string]: EmbeddedApp };
    tribes?: { [id: string]: EmbeddedTribe };
    factories?: { [id: string]: EmbeddedFactory };
}

export class EmbeddedUser {
    public id: string;
    public email: string;
    public fullName: string;
    public avatar: string;
    public isInternal: boolean;
    public isDeleted: boolean;

    constructor(user?: Partial<{
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        avatar: string;
        isInternal: boolean;
        deletedAt: string;
    }>) {
        this.id = user && user.id || '';
        this.email = user && user.email || '';
        this.fullName = user && `${user.firstName} ${user.lastName}` || '';
        this.avatar = user && user.avatar || '';
        this.isInternal = user && user.isInternal || false;
        this.isDeleted = user && Boolean(user.deletedAt);
    }
}

export class EmbeddedTribe {
    public id: string;
    public name: string;
    public color?: string;
    public avatar?: string;

    constructor(user?: Partial<{
        id: string;
        name: string;
        color: string;
        avatar: string;
    }>) {
        this.id = user?.id ?? '';
        this.name = user?.name ?? '';
        this.color = user?.color;
        this.avatar = user?.avatar;
    }
}

export class EmbeddedProject {
    public id: string;
    public name: string;
    public customer: string;

    constructor(project?: Partial<{id: string; name: string; customer: string}>) {
        this.id = project && project.id || '';
        this.name = project && project.name || '';
        this.customer = project && project.customer || '';
    }
}

export class EmbeddedFactory {
    public id: string;
    public name: string;

    constructor({ id = '', name = '' }: Partial<{ id: string; name: string }> = {}) {
        this.id = id;
        this.name = name;
    }
}

export class EmbeddedCell {
    public id: string;
    public name: string;

    constructor(cell?: Partial<{id: string; name: string}>) {
        this.id = cell && cell.id || '';
        this.name = cell && cell.name || '';
    }
}

export class EmbeddedCompany {
    public id: string;
    public name: string;
    public corporateName: string;

    constructor(company?: Partial<{id: string; name: string; corporateName: string}>) {
        this.id = company && company.id || '';
        this.name = company && company.name || '';
        this.corporateName = company && company.corporateName || '';
    }
}

export class EmbeddedApp {
    public id: string;
    public name: string;
    public logo: string;
    public category: AppCategory;
    public homepageUrl: string;

    constructor(app?: Partial<{ id: string; name: string; logo: string; category: AppCategory; homepageUrl: string }>) {
        this.id = app?.id ?? '';
        this.name = app?.name ?? '';
        this.logo = app?.logo ?? '';
        this.category = app?.category;
        this.homepageUrl = app?.homepageUrl ?? '';
    }
}

export function toCompanyEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedCompany {
    const company = embedded?.companies?.[data[field]];
    return new EmbeddedCompany(company);
}

export function toCompaniesEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedCompany[] {
    return data[field].map((value) => {
        const company = embedded?.companies?.[value];
        return new EmbeddedCompany(company);
    });
}

export function toUserEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedUser {
    const user = embedded?.users?.[data[field]];
    return new EmbeddedUser(user);
}

export function toUsersEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedUser[] {
    return (data[field] ?? []).map((value) => {
        const user = embedded?.users?.[value];
        return new EmbeddedUser(user);
    }).filter((user: EmbeddedUser) => user.id);
}

export function toTribeEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedTribe {
    const user = embedded?.tribes?.[data[field]];
    return new EmbeddedTribe(user);
}

export function toCellEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedCell {
    const user = embedded?.cells?.[data[field]];
    return new EmbeddedCell(user);
}

export function toFactoryEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedFactory {
    const factory = embedded?.factories?.[data[field]];
    return new EmbeddedFactory(factory);
}

export function toAppEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedApp {
    return new EmbeddedApp(embedded?.apps?.[data[field]]);
}

export function toAppsEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedApp[] {
    return data[field].map((value) => {
        const company = embedded?.apps?.[value];
        return new EmbeddedApp(company);
    });
}

export function toProjectsEmbedded(data: Object, field: string, embedded?: Embedded): EmbeddedProject[] {
    const projects = data[field];
    if (Array.isArray(projects)) {
        return projects.map((projectId) => {
            const project = embedded?.projects?.[projectId];
            return new EmbeddedProject(project);
        });
    }
    return [new EmbeddedProject(embedded?.projects?.[projects])];
}
